import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from './alert-dialog';

const ConfirmContext = createContext(null);

const DEFAULTS = {
  open: false,
  title: 'Are you sure?',
  description: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  destructive: false,
};

/**
 * App-wide confirmation dialog. Replaces the native window.confirm(), which
 * Chrome silently blocks inside cross-origin iframes (e.g. the preview), making
 * confirm-gated actions appear to "do nothing". Promise-based so call sites stay
 * tiny:  if (!(await confirm('Delete this?'))) return;
 */
export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULTS);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    const normalized = typeof opts === 'string' ? { description: opts } : opts;
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ ...DEFAULTS, ...normalized, open: true });
    });
  }, []);

  const settle = useCallback((result) => {
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
    setState((s) => ({ ...s, open: false }));
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) settle(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-dialog-title">{state.title}</AlertDialogTitle>
            {state.description ? (
              <AlertDialogDescription data-testid="confirm-dialog-description" className="whitespace-pre-line">
                {state.description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dialog-cancel" onClick={() => settle(false)}>
              {state.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={() => settle(true)}
              className={state.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
};
